import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useConsultEnabled } from "../lib/useConsultEnabled";
import { useFreeConsult } from "../lib/useFreeConsult";
import { shopApi, shopError } from "../lib/shop";
import { startCashfreePayment } from "../lib/cashfree";
import { PageLoader } from "../components/LoadingRing";
import { EbookReader } from "../components/EbookReader";
import { isPaid, renderTile } from "../components/dashboard/DashTiles";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
    const { customer, loading, logout } = useCustomerAuth();
    const { enabled: consultOn } = useConsultEnabled();
    const { eligible: freeConsult } = useFreeConsult();
    const [params] = useSearchParams();
    const previewId = params.get("layout") || "";
    const staffPreview = !!previewId && !!localStorage.getItem("rhm_token");

    const [orders, setOrders] = useState([]);
    const [reorders, setReorders] = useState([]);
    const [profile, setProfile] = useState({ name: "", phone: "" });
    const [reading, setReading] = useState(null);
    const [paying, setPaying] = useState("");

    useEffect(() => {
        if (!customer) return;
        setProfile({ name: customer.name || "", phone: customer.phone || "" });
        shopApi.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
        shopApi.get("/reorders").then(({ data }) => setReorders(data)).catch(() => {});
    }, [customer]);

    const { data: layout } = useQuery({
        queryKey: ["dashboard-layout", previewId],
        queryFn: async () => {
            const token = localStorage.getItem("rhm_token");
            const { data } = await shopApi.get("/dashboard/layout", {
                params: previewId ? { preview: previewId } : {},
                headers: previewId && token ? { Authorization: `Bearer ${token}` } : {},
            });
            return data;
        },
    });

    const { data: guides = [] } = useQuery({
        queryKey: ["my-library"],
        queryFn: async () => (await shopApi.get("/digital/library")).data,
        enabled: !!customer,
    });

    const { data: products = [] } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await fetch(`${BASE}/api/products`)).json(),
        staleTime: 120000,
    });

    const { data: posts = [] } = useQuery({
        queryKey: ["blog-posts-mini"],
        queryFn: async () => (await fetch(`${BASE}/api/blog/posts?limit=5`)).json(),
        staleTime: 300000,
    });

    if (loading) return <PageLoader />;
    if (!customer && !staffPreview) return <Navigate to="/account/login" replace />;

    const saveProfile = async () => {
        try {
            await shopApi.patch("/profile", profile);
            toast.success("Profile updated");
        } catch (e) { toast.error(shopError(e)); }
    };

    const onPay = async (orderNumber) => {
        setPaying(orderNumber);
        try {
            await startCashfreePayment(orderNumber);
        } catch (e) { toast.error(shopError(e)); setPaying(""); }
    };

    const ctx = {
        customer, products, posts, guides, reorders, profile, setProfile, saveProfile,
        purchases: orders.filter((o) => isPaid(o)),
        unpaid: orders.filter((o) => !isPaid(o) && !["cancelled", "returned"].includes(o.status)),
        onRead: setReading, onPay, paying, freeConsult, consultOn,
        preview: !!layout?.preview,
    };

    const zones = layout?.zones || { main: [], sidebar: [] };

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="account-page">
            <div className="mx-auto max-w-6xl px-6 md:px-12">
                {layout?.preview && (
                    <div className="mb-6 border border-gold/40 bg-gold/10 px-4 py-2 mono text-[10px] tracking-[0.16em] text-gold"
                        data-testid="layout-preview-banner">
                        PREVIEWING LAYOUT: {layout.name}
                    </div>
                )}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="eyebrow mb-3">My Account</div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight">
                            {customer?.name || customer?.email || "Customer preview"}
                        </h1>
                        <p className="text-sm text-zinc-500 mt-2">{customer?.email || "Staff preview — no customer signed in"}</p>
                    </div>
                    <div className="flex flex-wrap gap-3 self-start md:self-auto">
                        {consultOn && (
                            <Link to="/account/consultations" data-testid="account-consultations-link"
                                className="btn-pill btn-secondary">MY CONSULTATIONS</Link>
                        )}
                        {customer && (
                            <button onClick={logout} data-testid="account-logout" className="btn-pill btn-secondary">
                                <LogOut className="w-3.5 h-3.5" /> SIGN OUT
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-5 mt-8">
                    <div className="lg:col-span-2 space-y-5" data-testid="dashboard-main">
                        {zones.main.map((tile) => renderTile(tile, ctx))}
                    </div>
                    <div className="space-y-5" data-testid="dashboard-sidebar">
                        {zones.sidebar.map((tile) => renderTile(tile, ctx))}
                    </div>
                </div>
            </div>

            {reading && <EbookReader {...reading} onClose={() => setReading(null)} />}
        </div>
    );
}
